import React from "react";

import { isEnabled } from "utils/featureFlags";

import * as homeReducer from "containers/Home/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as deployReducer from "containers/Deployments/reducer";
import * as wizardReducer from "containers/Wizard/reducer";
import * as unifiedProjectReducer from "containers/UnifiedProject/reducer";
import * as globalReducer from "containers/App/reducer";

export const UPDATE_BASE = "business/UPDATE_BASE";
export const UPDATE_PROJECT = "business/UPDATE_PROJECT";
export const UPDATE_FIELD = "business/UPDATE_FIELD";

// getAdvancedDatasetOptions filters and creates the options for the header
export const SELECT_DATA_ADVANCED_OPTIONS = {
  FIELDS: "Fields",
  CALCULATED_FIELDS: "Calculated Fields",
  ADVANCED_MODELS: "Specialized AI Models",
  CLUSTER: "Cluster Chart"
};

export const getTrainingTitle = (state, id, showModel = false) => {
  const scenarios = projectReducer.getScenarios(state, id);
  const runs = projectReducer.getProjectRuns(state, id);
  const project = projectReducer.getProject(state, id);
  const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id) || (runs && runs[0] && runs[0].id);
  if (runs) {
    if (runs.find((elem) => elem.status !== "DONE" && elem.status !== "ERROR")) {
      return "Training...";
    } else {
      if (scenarios && showModel === false) {
        return scenarios.length + (scenarios.length === 1 ? " scenario" : " scenarios");
      } else if (project && project.segments && projectReducer.getIsOverallSegment(state, id, selectedRunId, true)) {
        return project.segments.length - 1 + " subgroups";
      }

      const capModel = projectReducer.getCapModel(state, id, selectedRunId);

      return capModel ? capModel.label : runs[0] && runs[0].models ? runs[0].models.length + " Evaluated" : "";
    }
  } else {
    return "NOT STARTED";
  }
};

export const getOptionalTitle = (state, id) => {
  const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);
  const setup = setupReducer.getSetup(state, selectedRunId);

  return setup === undefined || Object.keys(setup).length === 0 ? "..." : "OPTIONAL";
};

export const UNIFIED_PROJECT_STEPS = {
  SELECT_DATA: {
    id: "SELECT_DATA",
    label: "Use Case & Data",
    completeLabel: "Review Data & Blueprint",
    showCheck: true,
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);
      const setup = setupReducer.getSetup(state, selectedRunId);
      const selectedDataset = setupReducer.getDatasetFromSetup(state, selectedRunId);
      if (setup === undefined || Object.keys(setup).length === 0) {
        return "...";
      }
      if (selectedDataset) {
        return selectedDataset.name;
      } else {
        return "SELECT DATASET";
      }
    }
  },
  BUSINESS_OPTIMIZATION: {
    id: "BUSINESS_OPTIMIZATION",
    label: "Business Assumptions",
    completeLabel: "Business Assumptions",
    showCheck: true,
    showWarn: (state, id) => {
      return false;
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);

      return setup && setup.isFastTrain === true && scenarios && scenarios.length === 0 ? true : false;
    },
    disabled: (state, id) => {
      return getIsEditBlueprint(state, id) === true || id === "new" || projectReducer.getIsLoading(state, id);
    },
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const isScenarioDeployed = projectReducer.getIsScenarioDeployed(state, id);

      if (isScenarioDeployed) {
        return "COMPLETED";
      }
      return "REVIEW";
    }
  },
  BUSINESS_STRATEGY: {
    id: "BUSINESS_STRATEGY",
    label: "Business Strategy",
    completeLabel: "Business Strategy",
    showCheck: true,
    showWarn: (state, id) => {
      return false;
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);

      return setup && setup.isFastTrain === true && scenarios && scenarios.length === 0 ? true : false;
    },
    hidden: (state, id) => {
      const selectedRunId = projectReducer.getRunId(state, id);

      return setupReducer.getIsContinuous(state, selectedRunId);
    },
    disabled: (state, id) => {
      return getIsEditBlueprint(state, id) === true || id === "new" || projectReducer.getIsLoading(state, id);
    },
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getOverallRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);
      const revenueLabel = setupReducer.getAdvancedRevenueLabel(state, selectedRunId);
      const profitLabel = setupReducer.getNetImpactLabel(state, selectedRunId);
      const costLabel = setupReducer.getAdvancedCostLabel(state, selectedRunId);

      let objective = profitLabel ? profitLabel : "Impact";
      if (setup && setup.primaryBusinessConstraintType && revenueLabel && profitLabel && costLabel) {
        const currentObjective = setup.primaryBusinessConstraintType;
        switch (currentObjective) {
          case "profit":
            objective = profitLabel;
            break;
          case "revenue":
            objective = revenueLabel;
            break;
          case "cost":
            objective = costLabel;
            break;
        }
      }

      return `${objective.toUpperCase()}`;
    }
  },
  AI_MODEL: {
    id: "AI_MODEL",
    label: "AI Model",
    completeLabel: "AI Model",
    showCheck: true,
    disabled: (state, id) => {
      return getIsEditBlueprint(state, id) === true || id === "new" || projectReducer.getIsLoading(state, id);
    },
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);

      return getTrainingTitle(state, id, true);
    }
  },
  INTEGRATE: {
    id: "INTEGRATE",
    label: "Integration",
    disabled: (state, id) => {
      return (
        getIsEditBlueprint(state, id) === true ||
        id === "new" ||
        projectReducer.getIsLoading(state, id) ||
        !deployReducer.getProjectDeployment(state, id) ||
        !projectReducer.hasDeployedScenario(state, id)
      );
    },
    title: getOptionalTitle
  },
  MONITOR: {
    id: "MONITOR",
    label: "Monitoring & Retraining",
    disabled: (state, id) => {
      const scenarios = projectReducer.getScenarios(state, id);
      return (
        getIsEditBlueprint(state, id) === true ||
        id === "new" ||
        projectReducer.getIsLoading(state, id) ||
        !deployReducer.getProjectDeployment(state, id) ||
        !projectReducer.hasDeployedScenario(state, id)
      );
    },
    title: getOptionalTitle
  },
  FORECAST: unifiedProjectReducer.UNIFIED_PROJECT_STEPS.FORECAST
};

export const UNIFIED_PROJECT_STEP_ORDER = [
  UNIFIED_PROJECT_STEPS.SELECT_DATA.id,
  UNIFIED_PROJECT_STEPS.BUSINESS_OPTIMIZATION.id,
  UNIFIED_PROJECT_STEPS.BUSINESS_STRATEGY.id,
  UNIFIED_PROJECT_STEPS.AI_MODEL.id,
  UNIFIED_PROJECT_STEPS.INTEGRATE.id,
  UNIFIED_PROJECT_STEPS.MONITOR.id
];

export const UNIFIED_PROJECT_FORECAST_STEP_ORDER = [
  UNIFIED_PROJECT_STEPS.SELECT_DATA.id,
  UNIFIED_PROJECT_STEPS.FORECAST.id
];

export const DEFAULT_UNIFIED_PROJECT = {
  currentStep: UNIFIED_PROJECT_STEPS.SELECT_DATA.id,
  isSavingBlueprint: false,
  isEditBlueprint: false,
  businessMode: "OVERVIEW"
};

function businessReducer(
  state = {
    ["new"]: {
      ...DEFAULT_UNIFIED_PROJECT,
      currentStep: UNIFIED_PROJECT_STEPS.SELECT_DATA.id
    }
  },
  action
) {
  switch (action.type) {
    case UPDATE_BASE:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_PROJECT:
      return {
        ...state,
        [action.key]: action.value
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          [action.field]: action.value
        }
      };
    default:
      return state;
  }
}

// Selectors
export function isCalculatedFields() {
  return isEnabled("calculated-fields");
}

export function getBase(state) {
  return state.business;
}

export function getUnifiedProject(state, id) {
  return getBase(state)[id];
}

export function getCurrentStep(state, id) {
  const project = getUnifiedProject(state, id);
  return project && project.currentStep;
}

export function getMaxStep(state, id) {
  const project = getUnifiedProject(state, id);
  return project && project.maxStep;
}

export function getIsEditBlueprint(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).isEditBlueprint : undefined;
}

export function getIsAudit(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).isAudit : undefined;
}

export function getBusinessMode(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).businessMode : undefined;
}

export function getIsSavingBlueprint(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).isSavingBlueprint : undefined;
}

export function getIsInit(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).isInit : undefined;
}

export function getBlueprintId(state, id) {
  return projectReducer.getBlueprintId(state, id) !== undefined
    ? projectReducer.getBlueprintId(state, id)
    : setupReducer.getBlueprintId(state, "new") !== undefined
      ? setupReducer.getBlueprintId(state, "new")
      : "newBlueprint";
}

export function getEditBlueprintRunId(state, id) {
  const blueprintId =
    projectReducer.getBlueprintId(state, id) !== undefined ? projectReducer.getBlueprintId(state, id) : "newBlueprint";

  const runId =
    projectReducer.getInnerId(state, id) !== undefined ? projectReducer.getInnerId(state, id) : "editBlueprint";

  return runId;
}

export function getAdvancedDatasetOptions(state) {
  return Object.values(SELECT_DATA_ADVANCED_OPTIONS).filter((elem) => {
    if (elem === SELECT_DATA_ADVANCED_OPTIONS.CALCULATED_FIELDS) {
      return isCalculatedFields();
    }
    if (elem === SELECT_DATA_ADVANCED_OPTIONS.ADVANCED_MODELS) {
      return false;
    }
    return true;
  });
}

export function getIsHelpOpen(state) {
  return !loginReducer.getItem(state, "unifiedProjectHideHelp");
}

// Actions
export function updateIsHelpOpen() {
  return (dispatch, getState) => {
    if (!getIsHelpOpen(getState())) {
      dispatch(loginReducer.removeItem("unifiedProjectHideHelp"));
    } else {
      dispatch(loginReducer.storeItem("unifiedProjectHideHelp", true));
    }
    return !getIsHelpOpen(getState());
  };
}

export function updateUnifiedProject(id, project) {
  return {
    type: UPDATE_PROJECT,
    key: id,
    value: project
  };
}

export function updateMaxStep(id, maxStep) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "maxStep",
    value: maxStep
  };
}

export function updateIsSavingBlueprint(id, isSavingBlueprint) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "isSavingBlueprint",
    value: isSavingBlueprint
  };
}

export function updateIsAudit(id, isAudit) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "isAudit",
    value: isAudit
  };
}

export function updateBusinessMode(id, businessMode) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "businessMode",
    value: businessMode
  };
}

export function updateIsInit(id, isInit) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "isInit",
    value: isInit
  };
}

export function updateIsEditBlueprint(id, isEditBlueprint, skipReset = false, skipAll = false) {
  return (dispatch, getState) => {
    if (!skipAll) {
      const blueprintId = getBlueprintId(getState(), id);

      const runId = projectReducer.getInnerId(getState(), id);

      if (isEditBlueprint === true && blueprintId !== undefined) {
        dispatch(blueprintReducer.initSetup(blueprintId, "editBlueprint"));
      } else if (isEditBlueprint === true && blueprintId === undefined) {
        dispatch(blueprintReducer.initSetup(blueprintId, "editBlueprint"));
      }

      if (blueprintId === undefined || (blueprintId === "newBlueprint" && isEditBlueprint === true)) {
        const bp = blueprintReducer.getBlueprint(getState(), "newBlueprint");

        if (bp === undefined || Object.keys(bp).length === 1) {
          dispatch(
            blueprintReducer.updateBlueprint("newBlueprint", {
              id: "newBlueprint",
              wizard: {
                customFields: {},
                sensitive: {},
                advancedInputs: {
                  truePositive: undefined,
                  falsePositive: undefined,
                  trueNegative: undefined,
                  falseNegative: undefined
                }
              }
            })
          );
        }
      }

      const blueprint = {
        ...blueprintReducer.getBlueprint(getState(), blueprintId)
      };

      const editBlueprintId = getEditBlueprintRunId(getState(), id);

      const setup = { ...setupReducer.getSetup(getState(), editBlueprintId) };

      if (isEditBlueprint === true) {
        dispatch(setupReducer.updateSetup("tempSetup", setup));
        dispatch(blueprintReducer.updateBlueprint("tempDefault", blueprint));
      } else if (isEditBlueprint === false && skipReset === false) {
        const oldBlueprint = {
          ...blueprintReducer.getBlueprint(getState(), "tempDefault")
        };
        dispatch(blueprintReducer.updateBlueprint(blueprintId, oldBlueprint));

        dispatch(blueprintReducer.checkEvaluator(blueprintId, runId));
      }

      if (isEditBlueprint === false) {
        const oldSetup = {
          ...setupReducer.getSetup(getState(), "tempSetup")
        };
        dispatch(setupReducer.updateSetup(editBlueprintId, oldSetup));

        dispatch(blueprintReducer.checkEvaluator(blueprintId, runId));
      }
    }
    return dispatch({
      type: UPDATE_FIELD,
      key: id,
      field: "isEditBlueprint",
      value: isEditBlueprint
    });
  };
}

export function saveBlueprint(id, isApply) {
  return (dispatch, getState) => {
    const blueprintId = getBlueprintId(getState(), id);

    const runId = getEditBlueprintRunId(getState(), id);

    const setup = {
      ...setupReducer.getSetup(getState(), runId)
    };

    dispatch(updateIsSavingBlueprint(id, true));

    const editBlueprintSetup = setupReducer.getSetup(getState(), "editBlueprint");

    const label =
      projectReducer.getInnerId(getState(), id) === undefined
        ? editBlueprintSetup && editBlueprintSetup.label
          ? editBlueprintSetup.label
          : "New Blueprint"
        : setup && setup.label
          ? setup.label
          : "New Blueprint";

    return dispatch(blueprintReducer.saveBlueprint(blueprintId, label, setup, runId, !isApply))
      .then((retBlueprintId) => {
        dispatch(updateIsSavingBlueprint(id, false));
        dispatch(updateIsEditBlueprint(id, false, true));

        if (isApply) {
          dispatch(blueprintReducer.initSetup(retBlueprintId, runId === "editBlueprint" ? "new" : runId));
          dispatch(setupReducer.updateBlueprintId(runId === "editBlueprint" ? "new" : runId, retBlueprintId));
        } else {
          dispatch(resetStepProgress(id));
        }

        dispatch(blueprintReducer.deleteBlueprint("newBlueprint"));
      })
      .catch((e) => {
        dispatch(updateIsSavingBlueprint(id, false));
        dispatch(updateIsEditBlueprint(id, false, true));

        if (isApply) {
          dispatch(blueprintReducer.initSetup(blueprintId, "new"));
          dispatch(setupReducer.updateBlueprintId("new", blueprintId));
        } else {
          dispatch(resetStepProgress(id));
        }

        dispatch(blueprintReducer.deleteBlueprint("newBlueprint"));
      });
  };
}

export function getBlueprint(id) {
  return (dispatch, getState) => {
    return blueprintReducer.getBlueprint(getState(), id);
  };
}

export function updateCurrentStep(id, step) {
  return (dispatch, getState) => {
    const stepIndex = UNIFIED_PROJECT_STEP_ORDER.indexOf(step);

    const maxStep = getMaxStep(getState(), id);

    if (maxStep === undefined || stepIndex > maxStep) {
      dispatch(updateMaxStep(id, stepIndex));
    }

    if (step === UNIFIED_PROJECT_STEPS.BUSINESS_OPTIMIZATION.id) {
      dispatch(projectReducer.updateToOverall(id));
    }

    return dispatch({
      type: UPDATE_FIELD,
      key: id,
      field: "currentStep",
      value: step
    });
  };
}

export function selectStep(id, step) {
  return (dispatch, getState) => {
    dispatch(updateCurrentStep(id, step));
  };
}

export function resetStepProgress(id) {
  return (dispatch, getState) => {
    dispatch(updateCurrentStep(id, UNIFIED_PROJECT_STEP_ORDER[0]));
    dispatch(updateMaxStep(id, 0));
  };
}

export function startModelRun(id, runId, blueprintId, options) {
  return (dispatch, getState) => {
    const args = {
      runId: runId === undefined ? "new" : runId,
      blueprintId: blueprintId,
      isPostRun: id !== "new",
      options: options
    };

    //dispatch(selectStep(id, UNIFIED_PROJECT_STEPS.TRAIN.id));
    return !isNaN(blueprintId)
      ? wizardReducer.STEP_TYPE.SUCCESS.init(dispatch, getState, args)
      : wizardReducer.STEP_TYPE.ACADEMIC_SUCCESS.init(dispatch, getState, args);
  };
}

export function init(id, blueprintId, datasetId) {
  return (dispatch, getState) => {
    // dispatch({
    //   type: UPDATE_FIELD,
    //   key: id,
    //   field: "isEditBlueprint",
    //   value: false,
    // });

    if (id === "new") {
      if (getCurrentStep(getState(), id) !== UNIFIED_PROJECT_STEPS.SELECT_DATA.id) {
        dispatch(updateCurrentStep(id, UNIFIED_PROJECT_STEPS.SELECT_DATA.id));
      }
      const currSetup = setupReducer.getSetup(getState(), id);
      dispatch(blueprintReducer.init("new", blueprintId || currSetup.blueprintId || "academic"))
        .then((res) => {
          if (datasetId) {
            const dataset = homeReducer.getDataset(getState(), datasetId);
            if (dataset === undefined) {
              return dispatch(homeReducer.fetchDataset(datasetId));
            }
          }
          return Promise.resolve();
        })
        .then((res) => {
          if (isFinite(datasetId)) {
            dispatch(setupReducer.updateDatasetId("new", parseInt(datasetId), true));
          }
          if (getIsEditBlueprint(getState(), id) === true) {
            dispatch(updateIsEditBlueprint(id, true));
          }
        });
    } else {
      //dispatch(updateUnifiedProject(id, DEFAULT_UNIFIED_PROJECT));
      dispatch(updateIsInit(id, false));
      dispatch(globalReducer.updateCurrentProjectId(id));
      return dispatch(projectReducer.init(id)).then(() => {
        const pRunId = projectReducer.getOverallRunId(getState(), id);

        const innerId = projectReducer.getInnerId(getState(), id);

        const runId = innerId !== undefined ? innerId : pRunId;

        if (getCurrentStep(getState(), id) === undefined) {
          if (projectReducer.getIsForecastProject(getState(), id)) {
            dispatch(updateCurrentStep(id, UNIFIED_PROJECT_STEPS.FORECAST.id));
          } else {
            dispatch(updateCurrentStep(id, UNIFIED_PROJECT_STEPS.BUSINESS_OPTIMIZATION.id));
          }
        }

        if (runId) {
          return dispatch(blueprintReducer.init(pRunId, blueprintId));
        }
        return Promise.resolve();
      });
    }
  };
}

export function reset(id) {
  return (dispatch, getState) => {
    const blueprintIds = ["newBlueprint", "editBlueprint", "tempDefault"];
    const setupIds = ["tempSetup", "editBlueprint"];

    blueprintIds.forEach((bpId) => {
      const currBp = getBlueprintId(getState(), bpId);
      if (currBp) {
        dispatch(blueprintReducer.deleteBlueprint(bpId));
      }
    });

    setupIds.forEach((sId) => {
      const currSetup = setupReducer.getSetup(getState(), sId);
      if (currSetup) {
        dispatch(setupReducer.updateSetup(sId, undefined));
      }
    });

    if (getIsEditBlueprint(getState(), id)) {
      dispatch({
        type: UPDATE_FIELD,
        key: id,
        field: "isEditBlueprint",
        value: false
      });
    }
  };
}

export default businessReducer;
